/**
 * The live picture of what the client is doing and whether it can reach the
 * server. One tiny store, read by the topbar activity light, the offline
 * strip and the Diagnostics screen.
 *
 * It is deliberately *not* react-query: these are transport-level facts
 * (requests in flight, consecutive failures, last error) that sit underneath
 * the cache, and every query would otherwise have to report them itself.
 *
 * Connection states:
 *   online     the browser is online and the last request succeeded
 *   offline    `navigator.onLine` is false — nothing will reach the server
 *   unreachable the browser thinks it is online but requests keep failing
 *               (backend down, DNS, captive portal, VPN dropped)
 */
import { logger } from './logger';

export type Connection = 'online' | 'offline' | 'unreachable';

export interface ActivitySnapshot {
  /** Requests currently awaiting a response. */
  inFlight: number;
  connection: Connection;
  /** Consecutive network-level failures; 2+ flips to `unreachable`. */
  consecutiveFailures: number;
  /** Epoch ms of the last successful response, or null if none yet. */
  lastSuccessAt: number | null;
  /** Human summary of the last failure, for the strip and Diagnostics. */
  lastError: string | null;
  /** Correlation id of the last failed response, when the server sent one. */
  lastErrorCorrelationId: string | null;
  /** Rolling mean latency in ms over the last 20 completed requests. */
  latencyMs: number | null;
}

const UNREACHABLE_AFTER = 2;
const LATENCY_WINDOW = 20;

const log = logger.child('net');

/** `navigator` is absent in the Node test environment; treat that as online. */
function browserOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

let listeners = new Set<() => void>();
let latencies: number[] = [];
let snapshot: ActivitySnapshot = {
  inFlight: 0,
  connection: 'online',
  consecutiveFailures: 0,
  lastSuccessAt: null,
  lastError: null,
  lastErrorCorrelationId: null,
  latencyMs: null,
};

function emit(next: Partial<ActivitySnapshot>): void {
  snapshot = { ...snapshot, ...next };
  for (const l of listeners) l();
}

function meanLatency(): number | null {
  if (latencies.length === 0) return null;
  return Math.round(latencies.reduce((a, b) => a + b, 0) / latencies.length);
}

export const activity = {
  subscribe(fn: () => void): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },

  snapshot(): ActivitySnapshot {
    return snapshot;
  },

  /** Call when a request starts. */
  requestStarted(): void {
    emit({ inFlight: snapshot.inFlight + 1 });
  },

  /** Call when a request completes with a response (any status). */
  requestSucceeded(durationMs: number): void {
    latencies.push(durationMs);
    if (latencies.length > LATENCY_WINDOW) latencies.shift();
    const recovering = snapshot.connection !== 'online';
    if (recovering) log.info('Connection restored');
    emit({
      inFlight: Math.max(0, snapshot.inFlight - 1),
      connection: browserOnline() ? 'online' : 'offline',
      consecutiveFailures: 0,
      lastSuccessAt: Date.now(),
      latencyMs: meanLatency(),
    });
  },

  /**
   * Call when a request fails at the transport level (no response at all).
   * HTTP error *statuses* are not failures here — the server was reached.
   */
  requestFailed(message: string, correlationId?: string): void {
    const failures = snapshot.consecutiveFailures + 1;
    const connection: Connection =
      !browserOnline() ? 'offline' : failures >= UNREACHABLE_AFTER ? 'unreachable' : snapshot.connection;
    if (connection !== snapshot.connection) log.warn(`Connection ${connection}`, { failures, message });
    emit({
      inFlight: Math.max(0, snapshot.inFlight - 1),
      consecutiveFailures: failures,
      connection,
      lastError: message,
      lastErrorCorrelationId: correlationId ?? null,
    });
  },

  /** Wired to the browser's own online/offline events by `useActivity`. */
  setBrowserOnline(online: boolean): void {
    log.info(online ? 'Browser reports online' : 'Browser reports offline');
    emit({
      connection: online ? (snapshot.consecutiveFailures >= UNREACHABLE_AFTER ? 'unreachable' : 'online') : 'offline',
    });
  },

  /** Test seam: restores the module to a clean slate. */
  reset(): void {
    listeners = new Set();
    latencies = [];
    snapshot = {
      inFlight: 0,
      connection: 'online',
      consecutiveFailures: 0,
      lastSuccessAt: null,
      lastError: null,
      lastErrorCorrelationId: null,
      latencyMs: null,
    };
  },
};
